import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Select,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { IconAlertCircle, IconPlus, IconTrash } from '@tabler/icons-react';
import { AppModal } from '../../shared/components/AppModal';
import { ApiError } from '../../shared/api/problem';
import { useWarehouses } from '../warehouses/useWarehouses';
import {
  grantGroup,
  listGroups,
  listUserGroups,
  revokeGroup,
  type TenantUserDto,
} from './api';

interface Props {
  user: TenantUserDto | null;
  onClose: () => void;
  onSaved: () => void;
}

/** The locked group whose members reach everything, so scoping one is meaningless. */
const TENANT_ADMIN = 'TenantAdmin';

/** The "all warehouses" option's value. A Select cannot hold null, and '' reads as unset. */
const EVERYWHERE = 'all';

/** One thing this person is: a group, and where it applies. */
interface Grant {
  groupCode: string;
  /** Null means everywhere — an unscoped grant. */
  warehouseId: string | null;
}

function sameGrant(a: Grant, b: Grant): boolean {
  return a.groupCode === b.groupCode && a.warehouseId === b.warehouseId;
}

/**
 * What one person may do, and where.
 *
 * **This is a list of grants, not a grid of checkboxes.** Permissions resolve per warehouse now
 * (tenant 050), so a person is a set of (group, warehouse) pairs — "operator in Dallas, read-only
 * in Reno". The obvious rendering is a group × warehouse matrix, and it was the first thing tried:
 * four groups against the 3PL tenant's warehouses is a wall of checkboxes where almost every box
 * is unticked, and it grows with every site they take on. A list says the same thing in the words
 * somebody would use out loud.
 *
 * It replaced a screen with group checkboxes and a separate warehouse checkbox list. That shape
 * could not express this model at all: it said WHICH groups and WHICH warehouses, never which
 * group in which warehouse, and the endpoints behind its warehouse half no longer exist.
 *
 * **Changes are applied on Save, as a diff.** Grant and revoke are separate verbs one scope at a
 * time, so a row removed and re-added nets out to nothing rather than writing two audit rows for a
 * decision nobody made. It also means Cancel means something.
 */
export function UserAccessModal({ user, onClose, onSaved }: Props) {
  const { t } = useTranslation();

  const [grants, setGrants] = useState<Grant[]>([]);
  const [addGroup, setAddGroup] = useState<string | null>(null);
  const [addWarehouse, setAddWarehouse] = useState<string>(EVERYWHERE);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const groups = useQuery({
    queryKey: ['groups'] as const,
    queryFn: ({ signal }) => listGroups(signal),
    enabled: user !== null,
  });

  // The tenant's warehouses, not the ones the ADMIN can reach: this endpoint is deliberately
  // cross-warehouse, because granting somebody Reno while you are working in Dallas is the
  // ordinary way an administrator does this job.
  const warehouses = useWarehouses();

  const existing = useQuery({
    queryKey: ['user-groups', user?.userId ?? 'none'] as const,
    queryFn: ({ signal }) => listUserGroups(user!.userId, signal),
    enabled: user !== null,
  });

  // Seeded from the server each time the dialog opens, so a cancelled edit leaves nothing behind.
  useEffect(() => {
    if (!user) return;
    setSubmitError(null);
    setAddGroup(null);
    setAddWarehouse(EVERYWHERE);
  }, [user]);

  // ONE effect sets both the editable copy and the baseline it will be diffed against, so the
  // two can never disagree about what the user started with.
  //
  // `original` used to be a useMemo over existing.data. That looks equivalent and is not: `grants`
  // is state and survives a render where the query data is momentarily undefined, while a derived
  // baseline empties. Save then saw every existing grant as newly added and re-granted the lot —
  // an audit row against a decision nobody made, on a screen where the audit trail is the point.
  // It surfaced as this dialog's tests failing about one run in three.
  const [original, setOriginal] = useState<Grant[]>([]);

  useEffect(() => {
    if (!existing.data) return;
    const loaded = existing.data.map((g) => ({ groupCode: g.groupCode, warehouseId: g.warehouseId }));
    setGrants(loaded);
    setOriginal(loaded);
  }, [existing.data]);

  const groupName = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of groups.data ?? []) map.set(g.groupCode, g.displayName);
    return map;
  }, [groups.data]);

  const warehouseLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const w of warehouses.data ?? []) map.set(w.warehouseId, w.warehouseCode);
    return map;
  }, [warehouses.data]);

  const activeWarehouses = useMemo(
    () => (warehouses.data ?? []).filter((w) => w.isActive),
    [warehouses.data],
  );

  // A tenant administrator reaches everything, so offering a warehouse for that group would be
  // offering a choice the server ignores.
  const scopeless = addGroup === TENANT_ADMIN;

  const save = useMutation({
    mutationFn: async () => {
      if (!user) return;

      // allGrants, so a filled-in row that was never Added is still saved.
      const intended = allGrants();
      const added = intended.filter((g) => !original.some((o) => sameGrant(o, g)));
      const removed = original.filter((o) => !intended.some((g) => sameGrant(g, o)));

      for (const g of added) await grantGroup(user.userId, g.groupCode, g.warehouseId);
      for (const g of removed) await revokeGroup(user.userId, g.groupCode, g.warehouseId);
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (err) => {
      setSubmitError(
        err instanceof ApiError
          ? (err.problem.detail ?? err.problem.title ?? t('users.errors.saveFailed'))
          : t('users.errors.saveFailed'),
      );
    },
  });

  function add() {
    const candidate = pendingGrant();
    if (!candidate) return;
    setGrants((current) => [...current, candidate]);
    setAddGroup(null);
    setAddWarehouse(EVERYWHERE);
  }

  /**
   * The add row as a grant, when it holds one, or null.
   *
   * Save used to ignore it, so picking a group and a warehouse and pressing Save changes did
   * nothing at all — which is exactly what Mike hit trying to correct an account's access, right
   * after the same trap on the invite screen had created it wrong. Add is now only needed for a
   * SECOND grant.
   */
  function pendingGrant(): Grant | null {
    if (!addGroup) return null;
    const warehouseId = scopeless || addWarehouse === EVERYWHERE ? null : addWarehouse;
    const candidate: Grant = { groupCode: addGroup, warehouseId };
    // Already listed: the row on screen wins, and granting it twice is a duplicate.
    return grants.some((g) => sameGrant(g, candidate)) ? null : candidate;
  }

  /** Everything Save would write: the listed grants plus whatever the add row holds. */
  function allGrants(): Grant[] {
    const pending = pendingGrant();
    return pending ? [...grants, pending] : grants;
  }

  function remove(target: Grant) {
    setGrants((current) => current.filter((g) => !sameGrant(g, target)));
  }

  function describeWhere(grant: Grant): string {
    if (grant.groupCode === TENANT_ADMIN) return t('users.access.everywhereAdmin');
    if (grant.warehouseId === null) return t('users.access.everywhere');
    return warehouseLabel.get(grant.warehouseId) ?? t('users.access.unknownWarehouse');
  }

  const loading = groups.isLoading || existing.isLoading || warehouses.isLoading;
  // Computed from what SAVING would write, not from the committed list alone — otherwise the
  // warning claims the user reaches nothing while a filled-in row sits above it waiting to be
  // saved, which is the contradiction that made this screen untrustworthy in the first place.
  const intendedGrants = allGrants();
  const reachesNothing =
    intendedGrants.length === 0 ||
    (!intendedGrants.some((g) => g.groupCode === TENANT_ADMIN || g.warehouseId === null) &&
      intendedGrants.every((g) => !warehouseLabel.has(g.warehouseId ?? '')));

  return (
    <AppModal
      opened={user !== null}
      onClose={onClose}
      title={user ? t('users.editTitle', { name: user.displayName || user.email }) : ''}
      width="md"
    >
      {loading ? (
        <Center py="xl">
          <Loader color="invetrak" />
        </Center>
      ) : (
        <Stack gap="md">
          <div>
            <Text fw={500}>{user?.displayName}</Text>
            <Text size="sm" c="dimmed">
              {user?.email}
            </Text>
          </div>

          <Text size="sm" c="dimmed">
            {t('users.access.hint')}
          </Text>

          {grants.length === 0 ? (
            <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
              {t('users.access.none')}
            </Alert>
          ) : (
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('users.access.group')}</Table.Th>
                  <Table.Th>{t('users.access.where')}</Table.Th>
                  <Table.Th w={48} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {grants.map((g) => (
                  <Table.Tr key={`${g.groupCode}:${g.warehouseId ?? EVERYWHERE}`}>
                    <Table.Td>{groupName.get(g.groupCode) ?? g.groupCode}</Table.Td>
                    <Table.Td>
                      {g.warehouseId === null ? (
                        <Badge variant="light" color="blue">
                          {describeWhere(g)}
                        </Badge>
                      ) : (
                        describeWhere(g)
                      )}
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        onClick={() => remove(g)}
                        aria-label={t('users.access.remove', {
                          group: groupName.get(g.groupCode) ?? g.groupCode,
                          where: describeWhere(g),
                        })}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}

          <Group align="flex-end" gap="sm" wrap="wrap">
            <Select
              label={t('users.access.group')}
              placeholder={t('users.access.groupPlaceholder')}
              data={(groups.data ?? []).map((g) => ({ value: g.groupCode, label: g.displayName }))}
              value={addGroup}
              onChange={setAddGroup}
              searchable
              w={{ base: '100%', sm: 200 }}
            />
            <Select
              label={t('users.access.where')}
              // Disabled for a tenant administrator, with the reason on screen rather than a
              // silently ignored choice.
              description={scopeless ? t('users.access.adminIsEverywhere') : undefined}
              disabled={scopeless}
              data={[
                { value: EVERYWHERE, label: t('users.access.everywhere') },
                ...activeWarehouses.map((w) => ({
                  value: w.warehouseId,
                  label: `${w.warehouseCode} — ${w.displayName}`,
                })),
              ]}
              value={scopeless ? EVERYWHERE : addWarehouse}
              onChange={(v) => setAddWarehouse(v ?? EVERYWHERE)}
              allowDeselect={false}
              searchable
              w={{ base: '100%', sm: 220 }}
            />
            <Button
              variant="default"
              leftSection={<IconPlus size={16} />}
              onClick={add}
              disabled={!addGroup}
            >
              {t('users.access.add')}
            </Button>
          </Group>

          {/* Said rather than refused: an account being set up, or wound down, is a legitimate
              reason to have nothing here. But it means they can sign in and do nothing, and
              somebody should know that is what they just saved. */}
          {reachesNothing && grants.length > 0 && (
            <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
              {t('users.access.reachesNothing')}
            </Alert>
          )}

          {submitError && (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
              {submitError}
            </Alert>
          )}

          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose}>
              {t('shell.cancel')}
            </Button>
            <Button color="invetrak" onClick={() => save.mutate()} loading={save.isPending}>
              {t('shell.saveChanges')}
            </Button>
          </Group>
        </Stack>
      )}
    </AppModal>
  );
}
